import { Box, Button, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./SearchRequestFilters.styles";
import { sortHelper } from "src/utils/sortHelper";
import CircularLoaderFullPage from "src/components/common/CircularLoaderFullPage/CircularLoaderFullPage";
import SecureSyncAlert from "src/components/common/SecureSyncAlert/SecureSyncAlert";
import SecureSyncDatePicker from "src/components/common/SecureSyncDatePicker/SecureSyncDatePicker";
import SecureSyncSelect from "src/components/common/SecureSyncSelect/SecureSyncSelect";
import { getSelectedLanguageLocaleId } from "src/redux/languageSlice";
import {
    getComboBoxDescriptionForSearchRequests,
    getServiceDateForCustomerService,
    searchRequestService,
} from "src/services/requestServices";
import { formatDate, getDisplayFormat, getMinAndMaxDate, serviceDateMapper } from "src/utils/dateHelper";
import { dateFormats, dateTimeUnits } from "src/constants/dateFormatConstants";
import { requestHome } from "src/constants/routeConstants/requestRouterFullPaths";
import { noFlag } from "src/constants/serviceConstants";
import { useSecureSyncHotKeys } from "src/customHooks";
import { convertToLocaleUpperCase, getKeyBoardLabel } from "src/utils/commonHelper";
import { getShortCutKeys } from "src/constants/shortCutKeys";

const validationSchema = ({ t }) => {
    const { minDateForApi: minDate, maxDateForApi: maxDate, displayMaxDate, displayMinDate } = getMinAndMaxDate();
    return Yup.object().shape({
        requestTypeId: Yup.string(),
        requestId: Yup.number().typeError(t("messages.requestIdMustBeNumber")),
        requestStatusId: Yup.string(),
        serviceDateFrom: Yup.string().test("validateServiceDateFrom", (value, { createError, path, parent }) => {
            const isValidDate = dayjs(value, getDisplayFormat()).isValid();
            const isToDateValid = dayjs(parent.serviceDateTo, getDisplayFormat()).isValid();
            const isBeforeDate = dayjs(value, getDisplayFormat()).isSameOrBefore(
                dayjs(parent.serviceDateTo, getDisplayFormat())
            );
            const isOutSideMinDate = dayjs(value, getDisplayFormat()).isBefore(minDate);
            let message = "";
            if (value) {
                if (!isValidDate) {
                    message = t("messages.invalidDate");
                } else if (!isBeforeDate && isToDateValid) {
                    message = t("messages.fromDateLessThanCurrentDate", {
                        fromDate: formatDate(parent.serviceDateTo),
                    });
                } else if (isOutSideMinDate) {
                    message = t("messages.fromDateBetweenTheSpecificDate", {
                        from: t("common.from"),
                        minDate: displayMinDate,
                        maxDate: displayMaxDate,
                    });
                }
            }

            if (message) {
                return createError({
                    path,
                    message,
                });
            } else {
                return true;
            }
        }),
        serviceDateTo: Yup.string().test("validateServiceDateTo", (value, { createError, path, parent }) => {
            const isValidDate = dayjs(value, getDisplayFormat()).isValid();
            const isOutSideMaxDate = dayjs(value, getDisplayFormat()).isAfter(maxDate);
            let message = "";
            if (value) {
                if (!isValidDate) {
                    message = t("messages.invalidDate");
                } else if (isOutSideMaxDate) {
                    message = t("messages.toDateBetweenTheSpecificDate", {
                        to: t("common.to"),
                        minDate: displayMinDate,
                        maxDate: displayMaxDate,
                    });
                }
            }
            if (message) {
                return createError({
                    path,
                    message,
                });
            } else {
                return true;
            }
        }),
    });
};

const initialErrorState = {
    show: false,
    message: "",
};

const SearchRequestFilters = ({ onSearch, setSearchCriteria }) => {
    const shortCutKeys = getShortCutKeys();
    const { state: locationState } = useLocation();
    const navigate = useNavigate();
    const [requestTypeOptions, setRequestTypeOptions] = useState([]);
    const [requestStatusOptions, setRequestStatusOptions] = useState([]);
    const [isDropdownValuesLoading, setIsDropdownValuesLoading] = useState(false);
    const { t } = useTranslation();
    const defaultOption = { id: "all", value: "all", label: convertToLocaleUpperCase(t("common.all")) };
    const localeId = useSelector(getSelectedLanguageLocaleId);
    const [errorMessage, setErrorMessage] = useState(initialErrorState);
    const { minDateForApi: minServiceDate, maxDateForApi: maxServiceDate } = getMinAndMaxDate();
    const [serviceDates, setServiceDates] = useState([]);
    useSecureSyncHotKeys({ hotkeys: shortCutKeys.search, callback: () => handleSubmit() });
    useSecureSyncHotKeys({ hotkeys: shortCutKeys.reset, callback: () => handleReset() });

    useEffect(() => {
        const getComboBoxDescription = async () => {
            setIsDropdownValuesLoading(true);
            const { requestTypeValues, requestStatusValues } = await getComboBoxDescriptionForSearchRequests();
            setIsDropdownValuesLoading(false);
            if (requestTypeValues) {
                const newRequestTypeOptions = requestTypeValues.map((requestType) => ({
                    id: requestType.id,
                    value: requestType.id,
                    label: requestType.code_value,
                }));
                const sortedRequestType = sortHelper({
                    data: newRequestTypeOptions,
                    primaryProperty: "label",
                    sortOrder: "asc",
                });
                sortedRequestType.unshift({ ...defaultOption });
                setRequestTypeOptions(sortedRequestType);
            }

            if (requestStatusValues) {
                const newRequestStatusOptions = requestStatusValues.map((requestType) => ({
                    id: requestType.id,
                    value: requestType.id,
                    label: requestType.code_value,
                }));
                const sortedRequestStatus = sortHelper({
                    data: newRequestStatusOptions,
                    primaryProperty: "label",
                    sortOrder: "asc",
                });
                sortedRequestStatus.unshift({ ...defaultOption });
                setRequestStatusOptions(sortedRequestStatus);
            }
        };
        getComboBoxDescription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const restoreSearchResults = async () => {
            if (locationState) {
                const newFormValues = {
                    requestTypeId: locationState?.requestTypeId || defaultOption.value,
                    requestId: locationState?.requestId || "",
                    requestStatusId: locationState?.requestStatusId || defaultOption.value,
                    serviceDateFrom: locationState?.serviceDateFrom || "",
                    serviceDateTo: locationState?.serviceDateTo || "",
                };
                await setValues(newFormValues);
                onFindClick(newFormValues);
            }
        };
        restoreSearchResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const getServiceDateList = async () => {
            const response = await getServiceDateForCustomerService({
                fromDate: minServiceDate,
                toDate: maxServiceDate,
                rescheduleFlag: noFlag,
                addonCutoffFlag: "",
            });
            if (!response.failure) {
                if (response?.resultSet0?.length > 0) {
                    setServiceDates(serviceDateMapper(response?.resultSet0));
                }
            }
        };
        getServiceDateList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPageState = (existingPageState, keepPaginationInfo) => {
        const newNewPageState = { ...existingPageState };
        if (keepPaginationInfo) {
            newNewPageState.pageNumber = locationState?.pageNumber;
            newNewPageState.rowsPerPage = locationState?.rowsPerPage;
            newNewPageState.orderBy = locationState?.orderBy;
            newNewPageState.order = locationState?.order;
        }

        return newNewPageState;
    };

    const onFindClick = async (values, isButtonClick) => {
        //To get all request we need to pass empty value.
        const fromDate = dayjs(values.serviceDateFrom, getDisplayFormat());
        const toDate = dayjs(values.serviceDateTo, getDisplayFormat());
        setSubmitting(true);
        const requestTypeId = values.requestTypeId === defaultOption.value ? "" : values.requestTypeId;
        const requestStatusId = values.requestStatusId === defaultOption.value ? "" : values.requestStatusId;
        let serviceFromDate = minServiceDate;
        if (values.serviceDateFrom) {
            serviceFromDate = formatDate(fromDate, dateFormats.requestServiceDateFormat);
        } else if (values.serviceDateTo) {
            serviceFromDate = formatDate(toDate, dateFormats.requestServiceDateFormat);
            setFieldValue("serviceDateFrom", values.serviceDateTo);
        }
        let serviceToDate = maxServiceDate;
        if (values.serviceDateTo) {
            serviceToDate = formatDate(toDate, dateFormats.requestServiceDateFormat);
        } else if (values.serviceDateFrom) {
            serviceToDate = formatDate(fromDate, dateFormats.requestServiceDateFormat);
            setFieldValue("serviceDateTo", values.serviceDateFrom);
        }

        let payload = {
            namedSort: "ID",
            sortDescending: "N",
            languageLocaleId: localeId,
        };
        if (values.requestId) {
            payload.requestId = values.requestId;
        } else {
            payload = {
                ...payload,
                serviceDateFrom: serviceFromDate,
                serviceDateTo: serviceToDate,
                requestTypeId,
                requestStatusId,
                requestId: "",
            };
        }
        const pageState = getPageState({ ...values }, !isButtonClick);
        navigate(requestHome, {
            state: pageState,
            replace: true,
        });
        const requestData = await searchRequestService(payload);
        if (requestData[0]?.hasError) {
            setErrorMessage({ show: true, message: requestData[0].displayMessage });
            onSearch({ error: true });
        } else {
            const result = sortHelper({
                data: requestData,
                primaryProperty: "Request_Id",
                secondaryProperty: null,
                sortOrder: "asc",
            });
            setErrorMessage(initialErrorState);
            onSearch({ error: false, result });
            setSearchCriteria({
                serviceDateFrom: serviceFromDate,
                serviceDateTo: serviceToDate,
                requestTypeId,
                requestStatusId,
                requestId: values?.requestId || "",
            });
        }
        setSubmitting(false);
    };

    const {
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldTouched,
        setFieldValue,
        setSubmitting,
        setValues,
        touched,
        values,
    } = useFormik({
        initialValues: {
            requestTypeId: defaultOption.value,
            requestId: "",
            requestStatusId: defaultOption.value,
            serviceDateFrom: "",
            serviceDateTo: "",
            displayServiceDateFrom: "",
            displayServiceDateTo: "",
        },
        validationSchema: validationSchema({ t }),
        onSubmit: () => onFindClick(values, true),
    });

    const handleReset = () => {
        resetForm();
        navigate(requestHome, {
            state: {},
            replace: true,
        });
        onSearch({ error: true, result: [] });
    };

    return (
        <>
            <CircularLoaderFullPage show={isSubmitting || isDropdownValuesLoading} />
            {errorMessage.show && (
                <SecureSyncAlert
                    severity={"error"}
                    message={errorMessage.message}
                    onClose={() => setErrorMessage(initialErrorState)}
                />
            )}
            <form onSubmit={handleSubmit}>
                <Box sx={styles.searchContainer}>
                    <Typography sx={styles.filterDesc}>
                        <Trans
                            i18nKey={"searchRequests.filterDesc"}
                            values={{ buttonLabel: t("buttonLabels.search") }}
                        />
                    </Typography>
                    <Box sx={styles.searchFields}>
                        <SecureSyncSelect
                            label={t("formFields.requestType")}
                            options={requestTypeOptions}
                            name="requestTypeId"
                            value={values.requestTypeId}
                            onChange={handleChange}
                        />
                        <TextField
                            label={t("formFields.requestID")}
                            name="requestId"
                            value={values.requestId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors.requestId && touched.requestId)}
                            helperText={touched.requestId && errors.requestId}
                            inputProps={{ maxLength: 7 }}
                        />
                        <SecureSyncSelect
                            label={t("formFields.requestStatus")}
                            options={requestStatusOptions}
                            name="requestStatusId"
                            value={values.requestStatusId}
                            onChange={handleChange}
                        />
                        <div />
                        <SecureSyncDatePicker
                            name="serviceDateFrom"
                            value={values.serviceDateFrom}
                            label={t("formFields.serviceDateFrom")}
                            helperText={touched.serviceDateFrom && errors.serviceDateFrom}
                            error={Boolean(touched.serviceDateFrom && errors.serviceDateFrom)}
                            onChange={async (value) => {
                                await setFieldValue("serviceDateFrom", value);
                                setFieldTouched("serviceDateFrom", true, true);
                            }}
                            onBlur={handleBlur}
                            getDateProp={(dateProp) => {
                                const isServiceDate = serviceDates.find((serviceDate) =>
                                    serviceDate.isSame(dateProp, dateTimeUnits.day)
                                );
                                return { highlight: isServiceDate };
                            }}
                        />
                        <SecureSyncDatePicker
                            name="serviceDateTo"
                            value={values.serviceDateTo}
                            label={t("formFields.serviceDateTo")}
                            helperText={touched.serviceDateTo && errors.serviceDateTo}
                            error={Boolean(touched.serviceDateTo && errors.serviceDateTo)}
                            onChange={async (value) => {
                                await setFieldValue("serviceDateTo", value);
                                setFieldTouched("serviceDateTo", true, true);
                            }}
                            onBlur={handleBlur}
                            getDateProp={(dateProp) => {
                                const isServiceDate = serviceDates.find((serviceDate) =>
                                    serviceDate.isSame(dateProp, dateTimeUnits.day)
                                );
                                return { highlight: isServiceDate };
                            }}
                        />
                    </Box>
                    <Box sx={styles.searchButtons}>
                        <Button
                            variant="outlined"
                            type="reset"
                            onClick={() => {
                                handleReset();
                            }}
                            title={getKeyBoardLabel(shortCutKeys.reset)}
                        >
                            {t("buttonLabels.clear")}
                        </Button>
                        <Button variant="contained" type="submit" title={getKeyBoardLabel(shortCutKeys.search)}>
                            {t("buttonLabels.find")}
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
};

export default SearchRequestFilters;
